import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, doc, updateDoc, serverTimestamp, query, orderBy, onSnapshot } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, auth, storage } from "../firebase/firebaseConfig";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { FaLock, FaExclamationTriangle, FaSignOutAlt, FaTimes, FaCloudUploadAlt, FaFilePdf, FaCheckCircle, FaPrint, FaChevronUp, FaChevronDown, FaEdit } from "react-icons/fa";
import logoCiddec from "../magenes/LogoMexicoGente.png";

const guinda = "#611232";
const guindaLight = "#8b1d47";
const verde = "#137333";

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");

  const [activeTab, setActiveTab] = useState("ver_solicitudes"); 
  const [solicitudes, setSolicitudes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [foliosList, setFoliosList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedSolicitud, setSelectedSolicitud] = useState(null); 
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [expedienteEditando, setExpedienteEditando] = useState(null);
  const [folioEditando, setFolioEditando] = useState(null); 
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Suscripción a Solicitudes
      const qSol = query(collection(db, "solicitudes"), orderBy("createdAt", "desc"));
      const unsubsSol = onSnapshot(qSol, (snap) => {
        const arr = [];
        snap.forEach((doc) => arr.push({ id: doc.id, ...doc.data() }));
        setSolicitudes(arr);
      });

      // Suscripción a Expedientes
      const qExp = query(collection(db, "expedientes"), orderBy("createdAt", "desc"));
      const unsubsExp = onSnapshot(qExp, (snap) => {
        const arr = [];
        snap.forEach((doc) => arr.push({ id: doc.id, ...doc.data() }));
        setExpedientes(arr);
      });

      // Suscripción a Folios (Trámites)
      const qFol = query(collection(db, "tramites"), orderBy("createdAt", "desc"));
      const unsubsFol = onSnapshot(qFol, (snap) => {
        const arr = [];
        snap.forEach((doc) => arr.push({ id: doc.id, ...doc.data() }));
        setFoliosList(arr);
      });

      return () => { unsubsSol(); unsubsExp(); unsubsFol(); };
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, loginData.email, loginData.password);
      setLoginError("");
    } catch (err) {
      setLoginError("Correo o contraseña incorrectos.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const handlePrint = (f, e) => {
    e.stopPropagation();
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Reporte de Folio ${f.folio}</title>
          <style>
            @page { size: letter; margin: 1cm; }
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #333; line-height: 1.4; margin: 0; padding: 20px; font-size: 13px; }
            .header-container { display: flex; align-items: center; gap: 15px; border-bottom: 2px solid #611232; padding-bottom: 15px; margin-bottom: 20px; }
            .header-container img { height: 60px; }
            h1 { color: #611232; margin: 0; font-size: 18px; }
            .section { margin-bottom: 15px; page-break-inside: avoid; }
            .label { font-weight: 800; color: #611232; text-transform: uppercase; font-size: 11px; margin-bottom: 4px; letter-spacing: 0.5px;}
            .content { padding: 12px; background: #fdfdfd; border: 1px solid #ddd; border-radius: 5px; font-size: 13px; white-space: pre-wrap; text-align: justify; }
            .meta-info { display: flex; justify-content: space-between; margin-bottom: 20px; background: #f9f9f9; padding: 10px 15px; border-radius: 8px; border: 1px solid #eee; }
            .meta-item { display: flex; flex-direction: column; }
            .meta-label { font-size: 10px; color: #666; text-transform: uppercase; font-weight: bold; }
            .meta-value { font-size: 14px; font-weight: bold; color: #111; }
            .status { background: #611232; color: white; padding: 3px 10px; border-radius: 12px; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header-container">
            <h1>REPORTE DE ATENCIÓN CIUDADANA</h1>
          </div>
          <div class="meta-info">
            <div class="meta-item"><span class="meta-label">Folio</span><span class="meta-value">${f.folio}</span></div>
            <div class="meta-item"><span class="meta-label">Fecha</span><span class="meta-value">${f.fecha}</span></div>
            <div class="meta-item"><span class="meta-label">Estatus</span><span class="status">${f.estatus}</span></div>
          </div>
          <div class="section"><div class="label">Nombre de la Víctima</div><div class="content">${f.nombre || 'No especificado'}</div></div>
          ${f.cantidad ? `<div class="section"><div class="label">Cantidad a Entregar</div><div class="content">${f.cantidad}</div></div>` : ''}
          ${f.claveSeguridad ? `<div class="section"><div class="label">Clave de Seguridad</div><div class="content">${f.claveSeguridad}</div></div>` : ''}
          <div class="section"><div class="label">Declaración de la Víctima</div><div class="content">${f.declaracion || 'Sin declaración'}</div></div>
          <div class="section"><div class="label">Informe Jurídico</div><div class="content">${f.informe || 'Sin informe'}</div></div>
          ${f.resolucion ? `<div class="section"><div class="label">Resolución Final</div><div class="content">${f.resolucion}</div></div>` : ''}
          ${f.requerimiento ? `<div class="section"><div class="label">Requerimiento</div><div class="content">${f.requerimiento}</div></div>` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  if (authLoading) return null;

  // if (!isAuthenticated) {
  //   return (
  //     <LoginContainer>
  //       <LoginBox>
  //         <LoginLogo src="https://i.ibb.co/3mbM96C/logo-cajeme.png" alt="Logo Cajeme" />
  //         <Title style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Acceso Administrativo</Title>
  //         {authError && <ErrorBox>{authError}</ErrorBox>}
  //         <Form onSubmit={handleLogin}>
  //           <InputGroup>
  //             <label>Usuario</label>
  //             <input type="email" required value={email} onChange={e => setEmail(e.target.value)} />
  //           </InputGroup>
  //           <InputGroup>
  //             <label>Contraseña</label>
  //             <input type="password" required value={password} onChange={e => setPassword(e.target.value)} />
  //           </InputGroup>
  //           <BotonSubmit type="submit">INGRESAR</BotonSubmit>
  //         </Form>
  //       </LoginBox>
  //     </LoginContainer>
  //   );
  // }

  const renderTabContent = () => {
    if (activeTab === "ver_solicitudes") {
      const filtrados = solicitudes.filter(s => s.nombre?.toLowerCase().includes(searchTerm.toLowerCase()));
      return (
        <ListCard>
          <ListHeader>
            <Title>REGISTRO DE SOLICITUDES</Title>
            <SearchInput
              placeholder="Buscar por nombre"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </ListHeader>
          <Listado>
            {filtrados.map(sol => (
              <ItemRow key={sol.id}>
                <div>
                  <ItemName>{sol.nombre}</ItemName>
                  <ItemDate>{sol.fecha}</ItemDate>
                </div>
                <BotonVer onClick={() => setSelectedSolicitud(sol)}>VER</BotonVer>
              </ItemRow>
            ))}
            {filtrados.length === 0 && <p style={{color:'#888'}}>No hay solicitudes registradas.</p>}
          </Listado>
        </ListCard>
      );
    }
    
    if (activeTab === "subir_expediente") {
      return <SubirExpedienteForm />;
    }

    if (activeTab === "ver_expedientes") {
      const filtrados = expedientes.filter(s => s.nombre?.toLowerCase().includes(searchTerm.toLowerCase()));
      return (
        <ListCard>
          <ListHeader>
            <Title>REGISTRO DE EXPEDIENTES</Title>
            <SearchInput
              placeholder="Buscar por nombre"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </ListHeader>
          <Listado>
            {filtrados.map(exp => (
              <ItemRow key={exp.id}>
                <div>
                  <ItemName>{exp.nombre}</ItemName>
                  <ItemDate>{exp.fecha}</ItemDate>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <BotonVer onClick={() => setExpedienteEditando(exp)}>ACTUALIZAR</BotonVer>
                  <BotonVer onClick={() => setSelectedExpediente(exp)}>VER</BotonVer>
                </div>
              </ItemRow>
            ))}
            {filtrados.length === 0 && <p style={{color:'#888'}}>No hay expedientes registrados.</p>}
          </Listado>
        </ListCard>
      );
    }

    if (activeTab === "crear_folio") {
      return <CrearFolioForm />;
    }

    if (activeTab === "ver_folios") {
      const filteredFolios = foliosList.filter((f) => {
        const term = searchTerm.toLowerCase();
        return (
          (f.folio && f.folio.toLowerCase().includes(term)) ||
          (f.fecha && f.fecha.toLowerCase().includes(term))
        );
      });

      return (
        <ListCard>
          <ListHeader>
            <Title>REGISTRO DE FOLIOS</Title>
            <SearchInput
              placeholder="Buscar por folio o fecha"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </ListHeader>
          <FoliosGrid>
            {filteredFolios.length > 0 ? (
              filteredFolios.map((f) => (
                <FolioItem key={f.id} onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}>
                  <FolioHeader>
                    <div>
                      <FolioTitle>{f.folio}</FolioTitle>
                      <FolioDate>{f.fecha}</FolioDate>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <StatusBadge estatus={f.estatus}>{f.estatus}</StatusBadge>
                      <IconButton onClick={(e) => { e.stopPropagation(); setFolioEditando(f); }} title="Editar folio">
                        <FaEdit />
                      </IconButton>
                      <IconButton onClick={(e) => handlePrint(f, e)} title="Imprimir reporte">
                        <FaPrint />
                      </IconButton>
                      {expandedId === f.id ? <FaChevronUp color={guinda} /> : <FaChevronDown color={guinda} />}
                    </div>
                  </FolioHeader>
                  {expandedId === f.id && (
                    <FolioBody>
                      <DataSection>
                        <DataLabel>Nombre de la Víctima</DataLabel>
                        <DataText>{f.nombre || "Sin nombre"}</DataText>
                      </DataSection>
                      <DataSection>
                        <DataLabel>Cantidad a Entregar</DataLabel>
                        <DataText>{f.cantidad || "No especificada"}</DataText>
                      </DataSection>
                      <DataSection>
                        <DataLabel>Clave de Seguridad</DataLabel>
                        <DataText>{f.claveSeguridad || "Sin clave"}</DataText>
                      </DataSection>
                      <DataSection>
                        <DataLabel>Declaración de la Víctima</DataLabel>
                        <DataText>{f.declaracion || "Sin declaración"}</DataText>
                      </DataSection>
                      <DataSection>
                        <DataLabel>Informe Jurídico</DataLabel>
                        <DataText>{f.informe || "Sin informe"}</DataText>
                      </DataSection>
                      {f.resolucion && (
                        <DataSection>
                          <DataLabel>Resolución Final</DataLabel>
                          <DataText>{f.resolucion}</DataText>
                        </DataSection>
                      )}
                      {f.requerimiento && (
                        <DataSection>
                          <DataLabel>Requerimiento</DataLabel>
                          <DataText>{f.requerimiento}</DataText>
                        </DataSection>
                      )}
                    </FolioBody>
                  )}
                </FolioItem>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                No se encontraron folios
              </div>
            )}
          </FoliosGrid>
        </ListCard>
      );
    }
  };

  return (
    <AdminContainer>
      <AdminHeader>
        <LogoWrap>
          <img src={logoCiddec} alt="Logo" />
          <h1>Panel de Administración</h1>
        </LogoWrap>
        <NavTabs>
          <TabButton $active={activeTab === "ver_solicitudes"} onClick={() => { setActiveTab("ver_solicitudes"); setSearchTerm(""); }}>
            Ver solicitudes
          </TabButton>
          <TabButton $active={activeTab === "crear_folio"} onClick={() => setActiveTab("crear_folio")}>
            Crear folio
          </TabButton>
          <TabButton $active={activeTab === "subir_expediente"} onClick={() => setActiveTab("subir_expediente")}>
            Subir expediente
          </TabButton>
          <TabButton $active={activeTab === "ver_folios"} onClick={() => { setActiveTab("ver_folios"); setSearchTerm(""); }}>
            Ver folios
          </TabButton>
          <TabButton $active={activeTab === "ver_expedientes"} onClick={() => { setActiveTab("ver_expedientes"); setSearchTerm(""); }}>
            Ver expedientes
          </TabButton>
          <LogoutBtn onClick={handleLogout}><FaSignOutAlt /> Salir</LogoutBtn>
        </NavTabs>
      </AdminHeader>

      <ContentArea>
        {renderTabContent()}
      </ContentArea>

      {/* Modal Ver Solicitud */}
      {selectedSolicitud && (
        <ModalOverlay onClick={() => setSelectedSolicitud(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseModal onClick={() => setSelectedSolicitud(null)}><FaTimes /></CloseModal>
            <Title style={{textAlign:'center', marginBottom: '2rem'}}>SOLICITUD <FaCheckCircle color={guinda} /></Title>
            
            <ModalGrid>
              <InfoBox>{selectedSolicitud.nombre}</InfoBox>
              <InfoBox>{selectedSolicitud.telefono}</InfoBox>
              <InfoBox style={{gridColumn: '1 / -1'}}>{selectedSolicitud.correo}</InfoBox>
            </ModalGrid>

            {selectedSolicitud.archivoUrl && (
              <ArchivosContainer>
                <ArchivoThumb href={selectedSolicitud.archivoUrl} target="_blank">
                  <FaFilePdf size={50} color="white" />
                  <span>PDF/DOC</span>
                </ArchivoThumb>
              </ArchivosContainer>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal Ver Expediente */}
      {selectedExpediente && (
        <ModalOverlay onClick={() => setSelectedExpediente(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseModal onClick={() => setSelectedExpediente(null)}><FaTimes /></CloseModal>
            <Title style={{textAlign:'center', marginBottom: '2rem'}}>EXPEDIENTE <FaCheckCircle color={guinda} /></Title>
            
            <ModalGrid>
              <InfoBox>{selectedExpediente.nombre}</InfoBox>
              <InfoBox>{selectedExpediente.folio}</InfoBox>
              {selectedExpediente.claveSeguridad && (
                <InfoBox style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                  <strong>Clave de Seguridad:</strong> {selectedExpediente.claveSeguridad}
                </InfoBox>
              )}
            </ModalGrid>

            {selectedExpediente.pdfUrl && (
              <ArchivosContainer style={{justifyContent: 'center', marginTop: '2rem'}}>
                <ArchivoThumb href={selectedExpediente.pdfUrl} target="_blank">
                  <FaFilePdf size={50} color="white" />
                  <span>ABRIR PDF</span>
                </ArchivoThumb>
              </ArchivosContainer>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal Editar Folio */}
      {folioEditando && (
        <ModalOverlay onClick={() => setFolioEditando(null)}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '800px', display: 'flex', justifyContent: 'center' }}>
            <EditarFolioForm folioOriginal={folioEditando} onClose={() => setFolioEditando(null)} />
          </div>
        </ModalOverlay>
      )}

    </AdminContainer>
  );
}

function CrearFolioForm() {
  const [formData, setFormData] = useState({
    nombre: "",
    folio: "",
    cantidad: "",
    claveSeguridad: "",
    declaracion: "",
    informe: "",
    resolucion: "",
    requerimiento: "",
    estatus: "En proceso",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await addDoc(collection(db, "tramites"), {
        ...formData,
        fecha: new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }),
        createdAt: serverTimestamp(),
      });
      setSuccess(true);
      setFormData({ nombre: "", folio: "", cantidad: "", claveSeguridad: "", declaracion: "", informe: "", resolucion: "", requerimiento: "", estatus: "En proceso" });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError("Error al guardar el folio. Verifica la conexión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormCard>
      <div style={{ textAlign: "center", marginBottom: "3rem" }}>
        <Title>CREAR NUEVO FOLIO</Title>
        <p style={{ color: "#666" }}>Inicia un trámite oficial para una víctima de fraude.</p>
      </div>
      <Form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <FormGrid>
          <InputGroup>
            <label>NOMBRE DE LA VÍCTIMA</label>
            <input required placeholder="Nombre completo..." value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>NÚMERO DE FOLIO O EXPEDIENTE</label>
            <input required placeholder="Ej: NUC0000000AP" value={formData.folio} onChange={(e) => setFormData({ ...formData, folio: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>ESTATUS DEL TRÁMITE</label>
            <select value={formData.estatus} onChange={(e) => setFormData({ ...formData, estatus: e.target.value })}>
              <option value="En proceso">En proceso</option>
              <option value="Diligencias Abiertas">Diligencias Abiertas</option>
              <option value="Resuelto">Resuelto</option>
            </select>
          </InputGroup>
          <InputGroup>
            <label>CANTIDAD A ENTREGAR</label>
            <input placeholder="Ej: $50,000 MXN" value={formData.cantidad} onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>CLAVE DE SEGURIDAD</label>
            <input placeholder="Ej: ASDF-1234" value={formData.claveSeguridad} onChange={(e) => setFormData({ ...formData, claveSeguridad: e.target.value })} />
          </InputGroup>
        </FormGrid>
        <InputGroup>
          <label>DECLARACIÓN DE LA VÍCTIMA</label>
          <textarea rows="4" placeholder="Describe los hechos..." value={formData.declaracion} onChange={(e) => setFormData({ ...formData, declaracion: e.target.value })} />
        </InputGroup>
        <InputGroup>
          <label>INFORME JURÍDICO (USO INTERNO)</label>
          <textarea rows="4" placeholder="Análisis del área legal..." value={formData.informe} onChange={(e) => setFormData({ ...formData, informe: e.target.value })} />
        </InputGroup>
        <InputGroup>
          <label>RESOLUCIÓN FINAL</label>
          <textarea rows="3" placeholder="Dictamen o conclusión..." value={formData.resolucion} onChange={(e) => setFormData({ ...formData, resolucion: e.target.value })} />
        </InputGroup>
        <InputGroup>
          <label>REQUERIMIENTO</label>
          <textarea rows="3" placeholder="Documentos o acciones requeridas..." value={formData.requerimiento} onChange={(e) => setFormData({ ...formData, requerimiento: e.target.value })} />
        </InputGroup>
        
        {error && <ErrorBox>{error}</ErrorBox>}
        {success && <div style={{ color: verde, fontWeight: 700, textAlign: 'center' }}>Folio creado exitosamente</div>}
        
        <BotonSubmit type="submit" disabled={loading} style={{ alignSelf: 'center', marginTop: '1rem' }}>
          {loading ? "GUARDANDO..." : "CREAR FOLIO"}
        </BotonSubmit>
      </Form>
    </FormCard>
  );
}

function EditarFolioForm({ folioOriginal, onClose }) {
  const [formData, setFormData] = useState({
    nombre: folioOriginal.nombre || "",
    folio: folioOriginal.folio || "",
    cantidad: folioOriginal.cantidad || "",
    claveSeguridad: folioOriginal.claveSeguridad || "",
    declaracion: folioOriginal.declaracion || "",
    informe: folioOriginal.informe || "",
    resolucion: folioOriginal.resolucion || "",
    requerimiento: folioOriginal.requerimiento || "",
    estatus: folioOriginal.estatus || "En proceso",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await updateDoc(doc(db, "tramites", folioOriginal.id), {
        ...formData
      });
      onClose();
    } catch (err) {
      setError("Error al actualizar el folio. Verifica la conexión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormCard style={{ margin: 0, width: '100%', position: 'relative' }}>
      <CloseModal onClick={onClose} style={{ top: '2rem', right: '2.5rem' }}><FaTimes /></CloseModal>
      <div style={{ textAlign: "center", marginBottom: "3rem" }}>
        <Title>EDITAR FOLIO</Title>
        <p style={{ color: "#666" }}>Actualiza la información del trámite {folioOriginal.folio}</p>
      </div>
      <Form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <FormGrid>
          <InputGroup>
            <label>NOMBRE DE LA VÍCTIMA</label>
            <input required placeholder="Nombre completo..." value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>NÚMERO DE FOLIO O EXPEDIENTE</label>
            <input required placeholder="Ej: NUC0000000AP" value={formData.folio} onChange={(e) => setFormData({ ...formData, folio: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>ESTATUS DEL TRÁMITE</label>
            <select value={formData.estatus} onChange={(e) => setFormData({ ...formData, estatus: e.target.value })}>
              <option value="En proceso">En proceso</option>
              <option value="Diligencias Abiertas">Diligencias Abiertas</option>
              <option value="Resuelto">Resuelto</option>
            </select>
          </InputGroup>
          <InputGroup>
            <label>CANTIDAD A ENTREGAR</label>
            <input placeholder="Ej: $50,000 MXN" value={formData.cantidad} onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>CLAVE DE SEGURIDAD</label>
            <input placeholder="Ej: ASDF-1234" value={formData.claveSeguridad} onChange={(e) => setFormData({ ...formData, claveSeguridad: e.target.value })} />
          </InputGroup>
        </FormGrid>
        <InputGroup>
          <label>DECLARACIÓN DE LA VÍCTIMA</label>
          <textarea rows="4" placeholder="Describe los hechos..." value={formData.declaracion} onChange={(e) => setFormData({ ...formData, declaracion: e.target.value })} />
        </InputGroup>
        <InputGroup>
          <label>INFORME JURÍDICO (USO INTERNO)</label>
          <textarea rows="4" placeholder="Análisis del área legal..." value={formData.informe} onChange={(e) => setFormData({ ...formData, informe: e.target.value })} />
        </InputGroup>
        <InputGroup>
          <label>RESOLUCIÓN FINAL</label>
          <textarea rows="3" placeholder="Dictamen o conclusión..." value={formData.resolucion} onChange={(e) => setFormData({ ...formData, resolucion: e.target.value })} />
        </InputGroup>
        <InputGroup>
          <label>REQUERIMIENTO</label>
          <textarea rows="3" placeholder="Documentos o acciones requeridas..." value={formData.requerimiento} onChange={(e) => setFormData({ ...formData, requerimiento: e.target.value })} />
        </InputGroup>
        
        {error && <ErrorBox>{error}</ErrorBox>}
        
        <BotonSubmit type="submit" disabled={loading} style={{ alignSelf: 'center', marginTop: '1rem' }}>
          {loading ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
        </BotonSubmit>
      </Form>
    </FormCard>
  );
}

// Componente para Subir Expediente
function SubirExpedienteForm() {
  const [formData, setFormData] = useState({ nombre: "", folio: "", claveSeguridad: "" });
  const [archivo, setArchivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Límite de 2.5 MB
    const MAX_SIZE = 2.5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setError("El PDF es demasiado pesado (Máximo 2.5 MB). Por favor, comprímelo antes de subirlo.");
      setArchivo(null);
      e.target.value = ""; // Limpiar el input
    } else {
      setError("");
      setArchivo(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!archivo) {
      setError("Debes subir un archivo PDF del expediente.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const storageRef = ref(storage, `expedientes_oficiales/${Date.now()}_${archivo.name}`);
      const snapshot = await uploadBytes(storageRef, archivo);
      const pdfUrl = await getDownloadURL(snapshot.ref);

      await addDoc(collection(db, "expedientes"), {
        nombre: formData.nombre,
        folio: formData.folio,
        claveSeguridad: formData.claveSeguridad,
        pdfUrl,
        fecha: new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }),
        createdAt: serverTimestamp(),
      });
      
      setSuccess(true);
      setFormData({ nombre: "", folio: "", claveSeguridad: "" });
      setArchivo(null);
      document.getElementById('pdf-upload').value = ''; // Limpiar input
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error(err);
      setError("Error al subir expediente: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormCard>
      <div style={{ textAlign: "center", marginBottom: "3rem" }}>
        <IconoUploadWrapper><FaCloudUploadAlt /></IconoUploadWrapper>
        <Title>SUBIR EXPEDIENTE</Title>
        <p style={{ color: "#666" }}>Ingresa la información oficial de CENAC para el seguimiento del caso.</p>
      </div>

      <Form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <InputGroup>
            <label>NOMBRE COMPLETO</label>
            <input required placeholder="EJ: RAMIRO PEREZ JUAREZ" value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} />
          </InputGroup>
          <InputGroup>
            <label>FOLIO</label>
            <input required placeholder="EJ: NUC0000000AP" value={formData.folio} onChange={e => setFormData({...formData, folio: e.target.value})} />
          </InputGroup>
          <InputGroup>
            <label>CLAVE DE SEGURIDAD</label>
            <input required placeholder="EJ: RM0000" value={formData.claveSeguridad} onChange={e => setFormData({...formData, claveSeguridad: e.target.value})} />
          </InputGroup>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', height: '100%' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 800, color: guinda }}>EXPEDIENTE DIGITAL</label>
          <DropZone>
            <input type="file" id="pdf-upload" accept=".pdf" onChange={handleFileChange} style={{ display: "none" }} />
            <label htmlFor="pdf-upload" style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "center", padding: "1rem" }}>
              <FaCloudUploadAlt size={80} color="#ccc" />
              <span style={{ color: guinda, fontWeight: 700, marginTop: "1rem", textAlign: "center", fontSize: "0.9rem" }}>
                {archivo ? archivo.name : "SUBIR PDF (Máx. 2.5MB)"}
              </span>
            </label>
          </DropZone>
        </div>

        <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
          {error && <ErrorBox>{error}</ErrorBox>}
          {success && <div style={{ color: verde, fontWeight: 700, textAlign: 'center', marginBottom: '1rem' }}>Expediente subido con éxito</div>}
          <BotonSubmit type="submit" disabled={loading} style={{ width: '100%' }}>
            {loading ? "Subiendo..." : "SUBIR EXPEDIENTE"}
          </BotonSubmit>
        </div>
      </Form>
    </FormCard>
  );
}

// Componente para Editar Expediente
function EditarExpedienteForm({ expediente, onClose }) {
  const [formData, setFormData] = useState({
    nombre: expediente.nombre || "",
    folio: expediente.folio || "",
    claveSeguridad: expediente.claveSeguridad || ""
  });
  const [archivo, setArchivo] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(expediente.pdfUrl || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const MAX_SIZE = 2.5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setError("El PDF es demasiado pesado (Máximo 2.5 MB).");
      setArchivo(null);
      e.target.value = "";
    } else {
      setError("");
      setArchivo(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let currentPdfUrl = pdfUrl;
      
      if (archivo) {
        if (expediente.pdfUrl) {
          try {
            const oldPdfRef = ref(storage, expediente.pdfUrl);
            await deleteObject(oldPdfRef);
          } catch (e) {
            console.error("Error al borrar PDF anterior:", e);
          }
        }
        const storageRef = ref(storage, `expedientes_oficiales/${Date.now()}_${archivo.name}`);
        const snapshot = await uploadBytes(storageRef, archivo);
        currentPdfUrl = await getDownloadURL(snapshot.ref);
      } else if (!pdfUrl && expediente.pdfUrl) {
        try {
          const oldPdfRef = ref(storage, expediente.pdfUrl);
          await deleteObject(oldPdfRef);
        } catch (e) {
          console.error("Error al borrar PDF anterior:", e);
        }
      }

      await updateDoc(doc(db, "expedientes", expediente.id), {
        nombre: formData.nombre,
        folio: formData.folio,
        claveSeguridad: formData.claveSeguridad,
        pdfUrl: currentPdfUrl,
        fecha: new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }),
      });
      
      onClose();
    } catch (err) {
      console.error(err);
      setError("Error al actualizar expediente: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormCard style={{ boxShadow: 'none', padding: 0 }}>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <IconoUploadWrapper><FaCloudUploadAlt /></IconoUploadWrapper>
        <Title>ACTUALIZAR EXPEDIENTE</Title>
      </div>

      <Form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <InputGroup>
            <label>NOMBRE COMPLETO</label>
            <input required value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} />
          </InputGroup>
          <InputGroup>
            <label>FOLIO</label>
            <input required value={formData.folio} onChange={e => setFormData({...formData, folio: e.target.value})} />
          </InputGroup>
          <InputGroup>
            <label>CLAVE DE SEGURIDAD</label>
            <input required value={formData.claveSeguridad} onChange={e => setFormData({...formData, claveSeguridad: e.target.value})} />
          </InputGroup>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', height: '100%' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 800, color: guinda }}>EXPEDIENTE DIGITAL</label>
          <DropZone>
            <input type="file" id="pdf-upload-edit" accept=".pdf" onChange={handleFileChange} style={{ display: "none" }} />
            <label htmlFor="pdf-upload-edit" style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "center", padding: "1rem" }}>
              <FaCloudUploadAlt size={60} color="#ccc" />
              <span style={{ color: guinda, fontWeight: 700, marginTop: "1rem", textAlign: "center", fontSize: "0.9rem" }}>
                {archivo ? archivo.name : (pdfUrl ? "REEMPLAZAR PDF (Máx. 2.5MB)" : "SUBIR PDF (Máx. 2.5MB)")}
              </span>
            </label>
          </DropZone>
          {pdfUrl && !archivo && (
            <button 
              type="button"
              onClick={() => setPdfUrl(null)}
              style={{ background: '#d93025', color: 'white', border: 'none', padding: '0.5rem', borderRadius: '10px', cursor: 'pointer', marginTop: '0.5rem', fontWeight: 'bold' }}
            >
              Borrar PDF Actual
            </button>
          )}
        </div>

        <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
          {error && <ErrorBox>{error}</ErrorBox>}
          <BotonSubmit type="submit" disabled={loading} style={{ width: '100%' }}>
            {loading ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
          </BotonSubmit>
        </div>
      </Form>
    </FormCard>
  );
}

// Estilos
const AdminContainer = styled.div`
  min-height: 100vh;
  background: #f4f5f7;
`;

const AdminHeader = styled.header`
  background: white;
  padding: 1.5rem 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
    gap: 1rem;
    align-items: center;
  }
`;

const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  img { height: 45px; }
  h1 { margin: 0; color: ${guinda}; font-size: 1.5rem; font-weight: 900; }
`;

const NavTabs = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
    
    /* Ocultar barra de scroll para que se vea limpio */
    &::-webkit-scrollbar { display: none; }
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
`;

const TabButton = styled.button`
  background: transparent;
  color: ${guinda};
  border: 2px solid ${guinda};
  padding: 0.6rem 1.2rem;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  ${props => props.$active && `
    background: ${guinda};
    color: white;
  `}

  &:hover {
    ${props => !props.$active && `background: #fff0f5;`}
  }
`;

const LogoutBtn = styled.button`
  background: #ffebed;
  color: #d32f2f;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: #ffdce0;
  }
`;

const ContentArea = styled.div`
  max-width: 1000px;
  margin: 3rem auto;
  padding: 0 1.5rem;
`;

const FormCard = styled.div`
  background: white;
  padding: 4rem;
  border-radius: 20px;
  box-shadow: 0 15px 40px rgba(0,0,0,0.04);
`;

const Title = styled.h2`
  color: ${guinda};
  font-weight: 900;
  margin: 0;
  font-size: 1.6rem;
  letter-spacing: -0.5px;
`;

const ListCard = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 15px 40px rgba(0,0,0,0.04);
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SearchInput = styled.input`
  padding: 0.8rem 1.2rem;
  border: 2px solid #eaeaea;
  border-radius: 10px;
  width: 100%;
  max-width: 300px;
  font-family: inherit;
  &:focus { outline: none; border-color: ${guinda}; }
`;

const Listado = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const ItemRow = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  padding: 1.5rem 2rem;
  border-radius: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 5px 15px rgba(0,0,0,0.03); }
`;

const ItemName = styled.h3`
  margin: 0 0 0.4rem;
  color: ${guinda};
  font-weight: 800;
  font-size: 1.3rem;
`;

const ItemDate = styled.span`
  color: #888;
  font-size: 0.9rem;
  font-weight: 600;
`;

const BotonVer = styled.button`
  background: ${guinda};
  color: white;
  border: none;
  padding: 0.8rem 2.5rem;
  border-radius: 10px;
  font-weight: 800;
  cursor: pointer;
  &:hover { background: ${guindaLight}; }
`;

const Form = styled.form``;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  label { font-size: 0.85rem; font-weight: 800; color: ${guinda}; text-transform: uppercase; }
  input, select, textarea { padding: 1rem; border: 2px solid #eaeaea; border-radius: 10px; font-family: inherit; font-size: 1rem; }
  input:focus, select:focus, textarea:focus { outline: none; border-color: ${guinda}; }
`;

const DropZone = styled.div`
  flex: 1;
  border: 2px dashed #ccc;
  border-radius: 15px;
  transition: all 0.2s;
  &:hover { border-color: ${guinda}; background: #fafafa; }
`;

const BotonSubmit = styled.button`
  background: ${guinda};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-weight: 800;
  cursor: pointer;
  font-size: 1rem;
  &:disabled { opacity: 0.7; }
`;

const IconoUploadWrapper = styled.div`
  width: 70px; height: 70px;
  background: ${guinda}; color: white;
  border-radius: 20px;
  display: grid; place-items: center;
  font-size: 2rem;
  margin: 0 auto 1.5rem;
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.6); backdrop-filter: blur(5px);
  display: flex; justify-content: center; align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #f4f5f7;
  padding: 3rem;
  border-radius: 20px;
  width: 90%; max-width: 600px;
  position: relative;
`;

const CloseModal = styled.button`
  position: absolute; top: 1.5rem; right: 1.5rem;
  background: none; border: none; font-size: 1.5rem; color: #888; cursor: pointer;
`;

const ModalGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;
`;

const InfoBox = styled.div`
  background: white; border: 1px solid #eaeaea;
  padding: 1.2rem; border-radius: 12px;
  font-weight: 600; color: #444; font-size: 1.1rem;
  text-align: center;
`;

const ArchivosContainer = styled.div`
  margin-top: 2rem; background: #e9eaec;
  padding: 2rem; border-radius: 15px;
  display: flex; gap: 1rem; flex-wrap: wrap;
`;

const ArchivoThumb = styled.a`
  width: 120px; height: 140px;
  background: #d32f2f; border-radius: 12px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-decoration: none; color: white; gap: 0.5rem;
  transition: transform 0.2s;
  &:hover { transform: translateY(-3px); }
  span { font-weight: 800; font-size: 0.8rem; }
`;

const LoginContainer = styled.div` min-height: 100vh; display: grid; place-items: center; background: #f4f4f9; `;
const LoginBox = styled.div` background: white; padding: 3rem; border-radius: 20px; width: 90%; max-width: 400px; display: flex; flex-direction: column; align-items: center; `;
const LoginTitle = styled.h2` color: ${guinda}; font-weight: 900; display: flex; align-items: center; gap: 0.5rem; margin-bottom: 2rem; `;
const ErrorBox = styled.div` background: #fee; color: #c00; padding: 1rem; border-radius: 10px; display: flex; align-items: center; gap: 0.8rem; font-weight: 700; font-size: 0.9rem; margin-top: 1rem; `;

// Estilos adicionales para los folios antiguos
const FoliosGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1rem;
`;

const FolioItem = styled.div`
  background: #fdfdfd;
  border: 1px solid #eee;
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  &:hover {
    box-shadow: 0 8px 25px rgba(0,0,0,0.06);
    transform: translateY(-2px);
  }
`;

const FolioHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: #fafafa;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
`;

const FolioTitle = styled.h3`
  margin: 0 0 0.3rem 0;
  color: ${guinda};
  font-weight: 900;
  font-size: 1.3rem;
  letter-spacing: -0.5px;
`;

const FolioDate = styled.div`
  color: #666;
  font-size: 0.85rem;
  font-weight: 600;
`;

const FolioBody = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const DataSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #f5f5f5;
`;

const DataLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 800;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DataText = styled.p`
  margin: 0;
  color: #333;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const StatusBadge = styled.span`
  background: ${(props) => {
    switch (props.estatus) {
      case "Resuelto": return "#e6f4ea";
      case "Diligencias Abiertas": return "#e8f0fe";
      case "En proceso": return "#fff0f5";
      default: return "#f1f3f4";
    }
  }};
  color: ${(props) => {
    switch (props.estatus) {
      case "Resuelto": return verde;
      case "Diligencias Abiertas": return "#1967d2";
      case "En proceso": return guinda;
      default: return "#5f6368";
    }
  }};
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 800;
  white-space: nowrap;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 1.1rem;
  &:hover {
    background: #eee;
    color: ${guinda};
  }
`;
